import { RefObject, useEffect, useState } from "react";

/**
 * Scales, ticks and geometry for the hand-built Topic charts (plan D8),
 * modelled on deck-ui `PriceCurveChart`: plain functions from data to SVG
 * user units, no chart library.
 */

/** Maps `domain` linearly onto `range`. A flat domain maps to the middle of the range. */
export function linearScale(
  domain: [number, number],
  range: [number, number],
): (value: number) => number {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const span = d1 - d0;
  if (span === 0) {
    return () => (r0 + r1) / 2;
  }
  return (value: number) => r0 + ((value - d0) / span) * (r1 - r0);
}

export interface NiceDomain {
  min: number;
  max: number;
  ticks: Array<number>;
}

function niceStep(rough: number, isInteger: boolean): number {
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  const candidates = magnitude >= 10 || !isInteger ? [1, 2, 2.5, 5, 10] : [1, 2, 5, 10];
  const factor = candidates.find((candidate) => candidate >= normalized - 1e-9) ?? 10;
  const step = factor * magnitude;
  return isInteger ? Math.max(1, step) : step;
}

/**
 * An axis that covers `[min, max]` with about `count` steps of 1, 2, 2.5 or
 * 5 times a power of ten. Counts start at zero; integer steps by default.
 */
export function niceDomain(
  min: number,
  max: number,
  count = 4,
  isInteger = true,
): NiceDomain {
  const lo = Math.min(0, Number.isFinite(min) ? min : 0);
  let hi = Math.max(0, Number.isFinite(max) ? max : 0);
  if (hi === lo) {
    hi = lo + 1;
  }
  const step = niceStep((hi - lo) / Math.max(1, count), isInteger);
  const niceMin = Math.floor(lo / step) * step;
  const niceMax = Math.ceil(hi / step) * step;
  const ticks: Array<number> = [];
  for (let tick = niceMin; tick <= niceMax + step / 2; tick += step) {
    ticks.push(Math.round(tick * 1e6) / 1e6);
  }
  return { min: niceMin, max: niceMax, ticks };
}

/** Ticks for a fixed domain, e.g. -100 to 100 for net sentiment. */
export function fixedDomain(min: number, max: number, count = 4): NiceDomain {
  const step = (max - min) / Math.max(1, count);
  const ticks: Array<number> = [];
  for (let index = 0; index <= count; index += 1) {
    ticks.push(Math.round((min + step * index) * 1e6) / 1e6);
  }
  return { min, max, ticks };
}

/** x of point `index` of `count`, edge to edge across `[x0, x1]`; one point sits in the middle. */
export function pointX(index: number, count: number, x0: number, x1: number): number {
  if (count <= 1) {
    return (x0 + x1) / 2;
  }
  return x0 + (index / (count - 1)) * (x1 - x0);
}

/** At most `maxTicks` evenly spaced indexes out of `count`, always the first and the last. */
export function pickTickIndexes(count: number, maxTicks: number): Array<number> {
  if (count <= 0) {
    return [];
  }
  if (count <= maxTicks || maxTicks < 2) {
    return Array.from({ length: Math.min(count, Math.max(1, maxTicks)) }, (_, index) => index);
  }
  const step = Math.ceil((count - 1) / (maxTicks - 1));
  const picked: Array<number> = [];
  for (let index = 0; index < count - 1; index += step) {
    picked.push(index);
  }
  // Keep the last one; drop a neighbour that would crowd it.
  if (picked.length > 0 && count - 1 - picked[picked.length - 1] < step / 2) {
    picked.pop();
  }
  picked.push(count - 1);
  return picked.slice(0, maxTicks);
}

export interface SvgPoint {
  x: number;
  y: number;
}

/** A polyline `d`, broken into separate runs at each `null`. */
export function linePath(points: Array<SvgPoint | null>): string {
  let d = "";
  let isPenDown = false;
  for (const point of points) {
    if (point == null) {
      isPenDown = false;
      continue;
    }
    d += `${isPenDown ? "L" : "M"}${point.x},${point.y}`;
    isPenDown = true;
  }
  return d;
}

/**
 * Moves marks along one axis so any two sit at least `gap` apart, staying in
 * `[lower, upper]`. Returns the new positions in the input order; the
 * relative order of the marks never changes.
 */
export function nudgeApart(
  positions: Array<number>,
  gap: number,
  lower: number,
  upper: number,
): Array<number> {
  const order = positions
    .map((value, index) => ({ value, index }))
    .sort((a, b) => a.value - b.value || a.index - b.index);
  const placed = order.map((entry) => entry.value);
  for (let index = 1; index < placed.length; index += 1) {
    placed[index] = Math.max(placed[index], placed[index - 1] + gap);
  }
  const overflow = placed.length > 0 ? placed[placed.length - 1] - upper : 0;
  if (overflow > 0) {
    placed[placed.length - 1] -= overflow;
    for (let index = placed.length - 2; index >= 0; index -= 1) {
      placed[index] = Math.min(placed[index], placed[index + 1] - gap);
    }
  }
  if (placed.length > 0 && placed[0] < lower) {
    // More marks than fit: spread them evenly over the whole span instead.
    const span = upper - lower;
    const step = placed.length > 1 ? span / (placed.length - 1) : 0;
    for (let index = 0; index < placed.length; index += 1) {
      placed[index] = lower + step * index;
    }
  }
  const out = new Array<number>(positions.length);
  order.forEach((entry, rank) => {
    out[entry.index] = placed[rank];
  });
  return out;
}

/**
 * `nudgeApart` for line-end marks: only ends whose x positions sit within
 * `xReach` of each other (so their marks would touch) are pushed apart; ends
 * far apart on x keep their own height. Every result stays in `lower..upper`.
 */
export function nudgeEndsApart(
  ends: Array<SvgPoint>,
  gap: number,
  xReach: number,
  lower: number,
  upper: number,
): Array<number> {
  const order = ends
    .map((end, index) => ({ end, index }))
    .sort((a, b) => a.end.x - b.end.x || a.index - b.index);
  const out = new Array<number>(ends.length);
  let group: typeof order = [];
  const flush = () => {
    const ys = nudgeApart(
      group.map((entry) => entry.end.y),
      gap,
      lower,
      upper,
    );
    group.forEach((entry, rank) => {
      out[entry.index] = ys[rank];
    });
    group = [];
  };
  for (const entry of order) {
    const previous = group[group.length - 1];
    if (previous && entry.end.x - previous.end.x >= xReach) {
      flush();
    }
    group.push(entry);
  }
  if (group.length > 0) {
    flush();
  }
  return out;
}

const bucketDateFormatters = new Map<string, Intl.DateTimeFormat>();

/** A bucket start (`YYYY-MM-DD`, UTC) as a short date, read in UTC so no zone shifts the day. */
export function formatBucketDate(start: string, locale?: string): string {
  const key = locale ?? "";
  let formatter = bucketDateFormatters.get(key);
  if (formatter == null) {
    formatter = new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    });
    bucketDateFormatters.set(key, formatter);
  }
  const ms = Date.parse(`${start}T00:00:00Z`);
  return Number.isNaN(ms) ? start : formatter.format(ms);
}

/** Width used when the element cannot be measured (tests, first paint). */
export const SOCIAL_CHART_FALLBACK_WIDTH = 640;

/**
 * The element's content width, kept current with a `ResizeObserver`. A
 * fixed `width` skips measuring (tests, static renders).
 */
export function useChartWidth(ref: RefObject<HTMLElement>, width?: number): number {
  const [measured, setMeasured] = useState<number>(width ?? SOCIAL_CHART_FALLBACK_WIDTH);

  useEffect(() => {
    if (width != null) {
      setMeasured(width);
      return undefined;
    }
    const element = ref.current;
    if (element == null) {
      return undefined;
    }
    if (element.clientWidth > 0) {
      setMeasured(element.clientWidth);
    }
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width ?? 0;
      if (next > 0) {
        setMeasured(next);
      }
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref, width]);

  return width ?? measured;
}
